/**
 * CallDetailModal Component
 * 
 * Side panel (drawer) that displays detailed information about a specific call.
 * Slides in from the right side, matching the Retell AI dashboard design.
 * 
 * Features:
 * - Call details (date, agent, phone numbers, duration)
 * - Conversation analysis (success, sentiment, status, latency)
 * - Summary
 * - Full transcript with user-friendly formatting
 * - Audio player for call recording
 * 
 * @example
 * <CallDetailModal 
 *   callId="call_123"
 *   isOpen={true}
 *   onClose={() => setIsOpen(false)}
 * />
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getCallDetail } from '../../api/callsApi';
import { formatDate, formatDuration, formatPhoneNumber } from '../../utils/formatters';
import Badge from '../common/Badge';
import { DISCONNECTION_REASON_LABELS } from '../../utils/constants';

/**
 * CallDetailModal component (Side Panel)
 * 
 * @param {object} props - Component props
 * @param {string} props.callId - Call ID to fetch details for
 * @param {boolean} props.isOpen - Whether panel is open
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onNavigate - Navigate to next/previous call
 * @param {boolean} props.hasNext - Whether there's a next call
 * @param {boolean} props.hasPrevious - Whether there's a previous call
 */
const CallDetailModal = ({ callId, isOpen, onClose, onNavigate, hasNext, hasPrevious }) => {
  const [call, setCall] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch call details from API
   */
  const fetchCallDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getCallDetail(callId);
      setCall(data);
    } catch (err) {
      console.error('Error fetching call details:', err);
      setError(err.message || 'Failed to load call details');
    } finally {
      setIsLoading(false);
    }
  }, [callId]);

  /**
   * Fetch call details when panel opens
   */
  useEffect(() => {
    if (isOpen && callId) {
      fetchCallDetails();
    }
  }, [isOpen, callId, fetchCallDetails]);

  /**
   * Handle keyboard shortcuts
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      // Escape to close
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Arrow keys for navigation
      if (e.key === 'ArrowLeft' && hasPrevious && onNavigate) {
        e.preventDefault();
        onNavigate('previous');
      } else if (e.key === 'ArrowRight' && hasNext && onNavigate) {
        e.preventDefault();
        onNavigate('next');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, hasPrevious, hasNext, onNavigate]);

  /**
   * Prevent body scroll when modal is open
   */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-[600px] z-40 bg-gray-900 border-l border-gray-700 shadow-2xl overflow-hidden">
          <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-gray-800">
              <h2 className="text-lg font-semibold text-white">
                Call Details
              </h2>
              <div className="flex items-center gap-2">
                {/* Navigation Arrows */}
                <button
                  onClick={() => onNavigate && onNavigate('previous')}
                  disabled={!hasPrevious}
                  className={`p-2 rounded-lg transition-all ${
                    hasPrevious 
                      ? 'text-[#3AD0E6] hover:text-white hover:bg-gray-700 border border-gray-700 hover:border-[#3AD0E6]' 
                      : 'text-gray-600 cursor-not-allowed opacity-50'
                  }`}
                  title="Previous call (←)"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => onNavigate && onNavigate('next')}
                  disabled={!hasNext}
                  className={`p-2 rounded-lg transition-all ${
                    hasNext 
                      ? 'text-[#3AD0E6] hover:text-white hover:bg-gray-700 border border-gray-700 hover:border-[#3AD0E6]' 
                      : 'text-gray-600 cursor-not-allowed opacity-50'
                  }`}
                  title="Next call (→)"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <div className="w-px h-5 bg-gray-700 mx-1" />
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors rounded-lg p-1.5 hover:bg-gray-700"
                  title="Close"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3AD0E6]" />
              </div>
            )}

            {error && (
              <div className="p-6">
                <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
                  <p className="text-red-400">{error}</p>
                </div>
              </div>
            )}

            {call && (
              <div className="p-6 space-y-6">
                {/* TOP SECTION - Call Details */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Date/Time</p>
                      <p className="text-sm font-medium text-white">{formatDate(call.start_timestamp)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400 mb-1">Duration</p>
                      <p className="text-sm font-medium text-white">{formatDuration(call.duration_ms)}</p>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-700 pt-3">
                    <p className="text-xs text-gray-400 mb-1">Phone Call</p>
                    <p className="text-sm font-medium text-white">
                      {formatPhoneNumber(call.from_number)} → {formatPhoneNumber(call.to_number)} 
                      <span className="text-gray-400 ml-2">({call.direction})</span>
                    </p>
                  </div>
                </div>

                {/* CONVERSATION ANALYSIS */}
                <div className="border-t border-gray-700 pt-6">
                  <h3 className="text-base font-semibold text-white mb-3">Analysis</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-400">Call Successful</span>
                      <Badge variant="success" value={call.call_analysis?.call_successful}>
                        {call.call_analysis?.call_successful ? 'Yes' : 'No'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between py-2 border-t border-gray-700">
                      <span className="text-sm text-gray-400">Status</span>
                      <Badge variant="status" value={call.call_status}>
                        {call.call_status}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between py-2 border-t border-gray-700">
                      <span className="text-sm text-gray-400">User Sentiment</span>
                      <Badge variant="sentiment" value={call.call_analysis?.user_sentiment}>
                        {call.call_analysis?.user_sentiment || 'Unknown'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between py-2 border-t border-gray-700">
                      <span className="text-sm text-gray-400">Disconnection</span>
                      <span className="text-sm font-medium text-white">
                        {DISCONNECTION_REASON_LABELS[call.disconnection_reason] || call.disconnection_reason || '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* SUMMARY */}
                {call.call_analysis?.call_summary && (
                  <div className="border-t border-gray-700 pt-6">
                    <h3 className="text-base font-semibold text-white mb-3">Summary</h3>
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {call.call_analysis.call_summary}
                      </p>
                    </div>
                  </div>
                )}

                {/* AUDIO PLAYER */}
                <div className="border-t border-gray-700 pt-6">
                  <h3 className="text-base font-semibold text-white mb-3">Recording</h3>
                  {call.recording_url || call.recording_multi_channel_url ? (
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                      <audio 
                        controls 
                        className="w-full"
                        src={call.recording_url || call.recording_multi_channel_url}
                        style={{ height: '40px' }}
                      >
                        Your browser does not support the audio element.
                      </audio>
                      {call.recording_multi_channel_url && call.recording_url && (
                        <p className="text-xs text-gray-400 mt-2">
                          Multi-channel recording also available
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No recording available for this call</p>
                  )}
                </div>

                {/* TRANSCRIPT */}
                <div className="border-t border-gray-700 pt-6">
                  <h3 className="text-base font-semibold text-white mb-4">Transcript</h3>
                  <div className="space-y-3">
                    {call.transcript_object && call.transcript_object.length > 0 ? (
                      call.transcript_object.map((utterance, index) => {
                        // Convert seconds to mm:ss format
                        const formatTimestamp = (seconds) => {
                          const mins = Math.floor(seconds / 60);
                          const secs = Math.floor(seconds % 60);
                          return `${mins}:${secs.toString().padStart(2, '0')}`;
                        };
                        
                        const timestamp = utterance.words && utterance.words.length > 0 
                          ? formatTimestamp(utterance.words[0].start)
                          : '';
                        
                        return (
                          <div key={index} className="text-sm">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-2 flex-1">
                                <span className="text-[#3AD0E6] font-bold min-w-[70px]">
                                  {utterance.role === 'agent' ? 'Agent:' : 'Customer:'}
                                </span>
                                <span className="text-gray-300 flex-1">{utterance.content}</span>
                              </div>
                              {timestamp && (
                                <span className="text-gray-500 text-xs flex-shrink-0 mt-0.5">{timestamp}</span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : call.transcript ? (
                      <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
                        {call.transcript}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No transcription available</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
    </div>
  );
};

export default CallDetailModal;

